import { Injectable } from "@angular/core";
import { StateService } from "./../services/state.service";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsFunnel from "highcharts/modules/funnel";
import HighchartsPareto from "highcharts/modules/pareto";
import { clone, toTitleCase } from "../utils";
import * as echarts from "echarts";

HighchartsMore(Highcharts);
HighchartsFunnel(Highcharts);
HighchartsPareto(Highcharts);
@Injectable({
  providedIn: "root",
})
export class CreateChartService {
  private registeredCharts: echarts.ECharts[] = [];
  private resizeListenerAdded = false;

  constructor(private stateService: StateService) {}

  toTitleCase(str: string): string {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  nParseFloat(val: any) {
    if (typeof val === "string") {
      return val !== "" ? val : "-";
    } else if (this.isInt(val)) {
      return this.formatNumber(val);
    } else if (val && typeof val !== "string" && !this.isInt(val)) {
      const floatVal: any = this.stateService.nParseFloat(val);
      return this.formatNumber(floatVal);
    } else {
      return val || "-";
    }
  }

  isInt(num: any) {
    return Number(num) === num && num % 1 === 0;
  }

  formatNumber(num: number) {
    if (typeof num !== "number" || !isFinite(num)) return "";
    const sign = num < 0 ? "-" : "";
    const abs = Math.abs(num);
    const parts = abs.toString().split(".");
    const fractionDigits = parts[1] ? parts[1].length : 0;
    // Use Intl / toLocaleString which is safe and avoids backtracking regex
    const formatted = abs.toLocaleString("en-US", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return sign + formatted;
  }

  setChartData(
    edaData: any = [],
    x: any,
    y: any,
    type: any,
    bgColor: any,
    options?: any,
  ) {
    const labels = this.extractLabels(edaData, x);
    const values = this.extractValues(edaData, y, type);

    if (type === "pie") return this.buildPieData(labels, values, bgColor);
    if (type === "bar") return this.buildBarData(labels, values);
    if (type === "stacked_bar") return this.buildStackedBarData(labels);
    return this.buildDefaultData(labels, values, y, options);
  }

  private extractLabels(edaData: any[], x: any): any[] {
    return edaData.map((el: any) => el[x]);
  }

  private extractValues(edaData: any[], y: any, type: any): any[] {
    return edaData.map((el: any) =>
      type === "pie" ? el[y] : el[y]?.toFixed(),
    );
  }

  private buildPieData(labels: any[], values: any[], bgColor: any): any {
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor:
            bgColor && bgColor.length > 0
              ? bgColor
              : [
                  "#D65623",
                  "#F9DFB0",
                  "#F49C00",
                  "#A63132",
                  "#a09ae6",
                  "#de425b",
                  "#488f31",
                ],
          hoverBackgroundColor: ["#FFE082", "#81D4FA", "#A5D6A7"],
        },
      ],
    };
  }

  private buildBarData(labels: any[], values: any[]): any {
    return {
      labels,
      datasets: [
        {
          data: values,
          tension: 0.4,
          backgroundColor: "#49C2CA",
          borderColor: "#49C2CA",
        },
      ],
    };
  }

  private buildStackedBarData(labels: any[]): any {
    return {
      labels,
      datasets: [
        {
          label: "ABI",
          backgroundColor: "#325A6D",
          borderColor: "#325A6D",
          data: [],
        },
        {
          label: "NON ABI",
          backgroundColor: "#7ED4D9",
          borderColor: "#7ED4D9",
          data: [],
        },
      ],
    };
  }

  private buildDefaultData(
    labels: any[],
    values: any[],
    y: any,
    options?: any,
  ): any {
    const labelText =
      sessionStorage.getItem("expType") === "pho"
        ? y.toUpperCase()
        : y === "total_volume"
          ? "Volume"
          : "Revenue";
    const bg = this.selectBackground(options, y);
    const fill = options
      ? options.fill
      : sessionStorage.getItem("expType") === "pho"
        ? false
        : true;
    const borderColor =
      y === "total_revenue"
        ? "#E5B611"
        : y === "total_volume"
          ? "#325A6D"
          : "#E5B611";

    return {
      labels,
      datasets: [
        {
          label: labelText,
          data: values,
          backgroundColor: bg,
          fill,
          borderColor,
          borderWidth: 1,
          tension: 0.4,
        },
      ],
    };
  }

  private selectBackground(options: any, y: any) {
    if (options)
      return this.createLinearGradient(0, 0, 0, 200, [
        { offset: 0, color: "rgba(229, 182, 17, 1)" },
        { offset: 1, color: "rgba(229, 182, 17, 0)" },
      ]);
    if (y === "total_revenue")
      return this.createLinearGradient(0, 0, 0, 200, [
        { offset: 0, color: "rgba(229, 182, 17, 1)" },
        { offset: 1, color: "rgba(229, 182, 17, 0)" },
      ]);
    if (y === "total_volume")
      return this.createLinearGradient(0, 0, 0, 200, [
        { offset: 0, color: "rgba(50, 90, 109, 1)" },
        { offset: 1, color: "rgba(255, 255, 255, 0)" },
      ]);
    return "#E5B611";
  }

  generateColorShades(baseColor: string, count: number): string[] {
    const shades = [];
    const [r, g, b] = this.hexToRgb(baseColor);

    for (let i = 0; i < count; i++) {
      const factor = i / (count - 1); // Normalize factor between 0 and 1
      const shade = this.rgbToHex(
        Math.min(255, Math.max(0, Math.round(r + factor * (255 - r)))), // Adjust red
        Math.min(255, Math.max(0, Math.round(g + factor * (255 - g)))), // Adjust green
        Math.min(255, Math.max(0, Math.round(b + factor * (255 - b)))), // Adjust blue
      );
      shades.push(shade);
    }
    return shades;
  }

  // Convert hex to RGB
  hexToRgb(hex: string): [number, number, number] {
    hex = hex.replace(/^#/, "");

    // Parse the r, g, b values from the hex string using slice
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    return [r, g, b];
  }

  // Convert RGB to hex
  rgbToHex(r: number, g: number, b: number): string {
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  }

  createLinearGradient(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    colorStops: any[],
  ) {
    const ctx: any = document.createElement("canvas").getContext("2d");
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    colorStops.forEach((stop) =>
      gradient.addColorStop(stop.offset, stop.color),
    );
    return gradient;
  }

  setCompareChart(edaData: any = []) {
    let data: any;
    const labels = Object.keys(edaData);
    data = {
      labels: labels,
      datasets: [
        {
          label: "ABI",
          backgroundColor: "#325A6D",
          borderColor: "#325A6D",
          data: [],
        },
        {
          label: "NON ABI",
          backgroundColor: "#7ED4D9",
          borderColor: "#7ED4D9",
          data: [],
        },
      ],
    };
    labels.forEach((element) => {
      data.datasets[0].data.push(
        edaData[`${element}`]?.ABI?.revenue_contribution || 0,
      );
      data.datasets[1].data.push(
        edaData[`${element}`]?.NON_ABI?.revenue_contribution || 0,
      );
    });
    return data;
  }

  // Function for dynamic nesting walkway rates chart
  setLineBarCompareChart(
    densityCurve: any = [],
    walkAwayRate: any = [],
    x: any,
    y: any,
    type: any,
  ) {
    let data: any;

    data = {
      labels: walkAwayRate.map((item: any) => item[x]),
      datasets: [
        {
          type: "line",
          label: "",
          borderColor: "#EE8C45",
          borderWidth: 2,
          fill: false,
          yAxisID: "y1",
          xAxisID: "x1",
          tension: 0.4,
          data: densityCurve.map((item: any) => item.density),
          pointRadius: 0, // Hide the circles
          pointHoverRadius: 0, // Hide the hover effect circles
        },
        {
          type: "bar",
          label: "",
          backgroundColor: "#684088",
          data: walkAwayRate.map((item: any) => item[y]),
          borderColor: "white",
          borderWidth: 1,
          barPercentage: 1,
          yAxisID: "y",
          categoryPercentage: 1,
        },
      ],
    };

    return data;
  }
  setRecomCompareChart(edaData: any = [], labels: any) {
    let data: any;
    let lbls: any;
    const size = edaData.length;
    lbls = [];
    edaData.forEach((element: any) => {
      lbls.push(element.cluster_name);
    });
    data = {
      labels: lbls,
      datasets: [
        {
          label: "",
          backgroundColor: "#7FD4B7",
          borderColor: "#7FD4B7",
          data: [],
          barPercentage: 1,
          borderWidth: 0,
          //  categoryPercentage: 0.5
        },
        {
          label: "",
          backgroundColor: "#706ACA",
          borderColor: "#706ACA",
          data: [],
          barPercentage: 1,
          borderWidth: 0,
          //   categoryPercentage: 0.5
        },
      ],
    };

    edaData.forEach((ele: any, j: any) => {
      labels.forEach((element: any, i: any) => {
        data.datasets[i].data.push(+ele[`${element}`]?.replace("%", ""));
      });
    });
    return data;
  }
  setSimCompareChart(edaData: any = [], labl: any, isPercentage: boolean) {
    if (edaData.length === 1) {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      for (let index = 0; index < 3; index++) {
        temp.push(edaData[0].cluster_name);
      }

      data = {
        labels: labels,
        datasets: this.formDatas(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      const labls = labl;
      data.labels = data.graphdata.map(function (el: any) {
        return el.cluster_name;
      });
      labls.forEach(function (value: any, j: any) {
        edaData.forEach((element: any, i: string | number) => {
          const tem = isPercentage
            ? element[`${value}`].replace("%", "")
            : element[`${value}`];
          data.datasets[j].data[j] = +tem || 0;
        });
      });
      return data;
    } else {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      edaData.forEach((element: any) => {
        temp.push(element.cluster_name);
      });
      data = {
        labels: ["", "", ""],
        datasets: this.formData(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      const labls = labl;

      labls.forEach(function (value: any) {
        edaData.forEach((element: any, i: string | number) => {
          const tem = isPercentage
            ? element[`${value}`].replace("%", "")
            : element[`${value}`];
          data.datasets[i].data.push(+tem || 0);
        });
      });

      return data;
    }
  }
  setSimCompareChartTop3(edaData: any = [], labl: any, isPercentage: boolean) {
    if (edaData.length === 1) {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      labl = [...new Set(labl)];
      for (let index = 0; index < labl.length; index++) {
        temp.push(edaData[0].cluster_name);
      }

      data = {
        labels: labels,
        datasets: this.formDatas(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      const labls = [...new Set(labl)];
      data.labels = data.graphdata.map(function (el: any) {
        return el.cluster_name;
      });
      labls.forEach(function (value: any, j: any) {
        edaData.forEach((element: any, i: string | number) => {
          if (element[`${value}`] !== undefined) {
            const tem = isPercentage
              ? element[`${value}`].replace("%", "")
              : element[`${value}`];
            data.datasets[j].data[j] = +tem || 0;
          } else {
            data.datasets[j].data[j] = 0;
          }
        });
      });
      return data;
    } else {
      let data: any;
      let labels: any;
      labels = [];
      let temp: any;
      temp = [];
      edaData.forEach((element: any) => {
        temp.push(element.cluster_name);
      });
      data = {
        labels: labels,
        datasets: this.formData(temp),
        graphdata: [],
      };
      data.datasets.forEach((element: any) => {
        data.graphdata.push({
          cluster_name: element.clustername,
          clusterColor: element.backgroundColor,
        });
      });
      const labls = [...new Set(labl)];
      data.labels = data.graphdata.map(function (el: any) {
        return el.cluster_name;
      });
      labls.forEach(function (value: any) {
        edaData.forEach((element: any, i: string | number) => {
          if (element[`${value}`] !== undefined) {
            const tem = isPercentage
              ? element[`${value}`].replace("%", "")
              : element[`${value}`];
            data.datasets[i].data[i] = +tem || 0;
          } else {
            data.datasets[i].data[i] = 0;
          }
        });
      });
      return data;
    }
  }
  formData(temp: any) {
    let tem: any;
    tem = [];
    temp.forEach((element: any, i: any) => {
      tem.push({
        label: "",
        backgroundColor: this.getAnyOne(i),
        borderColor: "#1E88E5",
        data: [],
        barPercentage: 1,
        borderWidth: 0,
        clustername: element,
        //categoryPercentage: 0.5
      });
    });
    return tem;
  }
  formDatas(temp: any) {
    let tem: any;
    tem = [];
    temp.forEach((element: any, i: any) => {
      tem.push({
        label: "",
        backgroundColor: this.getAnyOne(i),
        borderColor: "#1E88E5",
        data: [],
        barPercentage: 1,
        // categoryPercentage: 0.6,
        borderWidth: 0,
        clustername: element,
        // categoryPercentage: 1
      });
    });
    return tem;
  }
  setLineCompareChart(edaData: any = [], x: any, y: any) {
    let data: any;
    const labels = edaData[0].map(function (el: any) {
      return el[x];
    });
    const uniqlabels = [...new Set(labels)];
    let years: any[];
    years = [];
    edaData.forEach((element: any) => {
      years.push(element[0].year);
    });
    const uniq = [...new Set(years)];
    data = {
      labels: uniqlabels,
      datasets: [],
    };
    edaData.forEach((element: any, i: any) => {
      const values = element.map(function (el: any) {
        return el[y] === 0 ? null : el[y];
      });

      const val = values.map(function (el: any) {
        return el === undefined ? null : el;
      });

      const obj = {
        label: uniq[i],
        backgroundColor: this.getLineChartColor(i),
        borderColor: this.getLineChartColor(i),
        borderWidth: 0.8,
        data: val,
      };
      data.datasets.push(obj);
    });

    //  }
    return data;
  }

  getLineChartColor(index: number) {
    return [
      "#FCC7C3",
      "#D63B2F",
      "#FAA19B",
      "#F88279",
      "#F66257",
      "#F44336",
      "#B73229",
      "#992A22",
      "#7A221B",
    ][index];
  }

  getAnyOne(index: number) {
    //new design colours
    return ["#C7972A", "#F7D385", "#FDC039", "#F7E6AB", "#EED065", "#E5B629"][
      index
    ];
  }

  getRandomColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }
  createWaterFallChart(
    title: string,
    data: any[],
    toolTip: any,
    type: any,
    xAxisName: string,
  ) {
    let newData = [];
    const label: any = [];
    if (data[0][type] === "total") {
      const totalObj = data.shift();
      const withoutTotalData = data;
      newData = withoutTotalData;
      newData.push(totalObj);
    } else {
      newData = data;
    }
    newData.forEach((element: any) => {
      label.push(element[type]);
    });
    const option = {
      title: {
        text: "",
        subtext: "",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          const tar = params[1];
          const value = tar.value;
          let result;
          if (Math.abs(+value) >= 1000000000) {
            result =
              (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
          } else if (Math.abs(+value) >= 1000000) {
            result = (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
          } else if (Math.abs(+value) >= 1000) {
            result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
          } else {
            result = value;
          }
          if (title === "Volume") {
            return tar.name + "<br/>" + tar.seriesName + " : " + result;
          } else {
            return tar.name + "<br/>" + tar.seriesName + " : " + "$" + result;
          }
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        name: xAxisName,
        nameLocation: "middle",
        nameGap:
          xAxisName === "PTC SEGMENT"
            ? 60
            : xAxisName === "MANUFACTURER"
              ? 40
              : 30,
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          rotate: 30,
        },
        splitLine: { show: false },
        data: label,
      },
      yAxis: [
        {
          type: "value",
          name:
            title === "Volume" ? "Volume contribution" : "Revenue Contribution",
          nameLocation: "middle",
          nameGap: title === "Volume" ? 40 : 54,
          // min: 0,
          // max: 25,
          position: "left",
          axisLabel: {
            formatter: function (value: any) {
              let result;
              if (Math.abs(+value) >= 1000000000) {
                result =
                  (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
              } else if (Math.abs(+value) >= 1000000) {
                result =
                  (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
              } else if (Math.abs(+value) >= 1000) {
                result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
              } else {
                result = value;
              }
              if (title === "Volume") {
                return result;
              } else {
                return "$" + result;
              }
            },
          },
        },
      ],
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "Total",
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: newData.map((a: { start_point: any }) => a.start_point),
        },
        {
          name: toolTip,
          type: "bar",
          stack: "Total",
          label: {
            show: false,
            position: "inside",
            formatter: function (d: any) {
              if (Math.abs(+d.data) >= 1000000000) {
                return (
                  (+d.data / 1000000000).toFixed().replace(/\.0$/, "") + " B"
                );
              }
              if (Math.abs(+d.data) >= 1000000) {
                return (+d.data / 1000000).toFixed().replace(/\.0$/, "") + " M";
              }
              if (Math.abs(+d.data) >= 1000) {
                return (+d.data / 1000).toFixed().replace(/\.0$/, "") + " K";
              }
              return d.data;
            },
          },
          color: "#ec7063",
          data: newData.map((a: { add_points: any }) => a.add_points),
        },
      ],
    };
    return option;
  }
  createSimulationWaterFallChart(
    title: string,
    data: any[],
    toolTip: any,
    type: any,
  ) {
    let newData = [];
    const label: any = [];
    if (data[0] !== undefined && data[0].label === "total") {
      const totalObj = data.shift();
      const withoutTotalData = data;
      newData = withoutTotalData;
      newData.push(totalObj);
    } else {
      newData = data;
    }
    newData.forEach((element: any) => {
      label.push(element.label);
    });
    const option = {
      title: {
        text: "",
        subtext: "",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          const tar = params[1];
          const value = tar.value;
          let result;
          if (Math.abs(+value) >= 1000000000) {
            result =
              (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
          } else if (Math.abs(+value) >= 1000000) {
            result = (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
          } else if (Math.abs(+value) >= 1000) {
            result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
          } else {
            result = value;
          }
          if (toolTip === "VOLUME" || toolTip === "SKU") {
            return tar.name + "<br/>" + result;
          } else {
            return tar.name + "<br/>" + "$" + result;
          }
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        name: "",
        nameLocation: "middle",
        nameGap: type === "Parent_SKU" ? 60 : 40,
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          rotate: 30,
          width: "90",
          overflow: "truncate", // or 'break' to continue in a new line
          interval: 0,
        },
        splitLine: { show: false },
        data: label,
      },
      yAxis: [
        {
          type: "value",
          name: "",
          // min: 0,
          // max: 25,
          //  position: 'left',
          nameLocation: "middle",
          nameGap: 40,
          axisLabel: {
            formatter: function (value: any) {
              let result;
              if (Math.abs(+value) >= 1000000000) {
                result =
                  (+value / 1000000000).toFixed(1).replace(/\.0$/, "") + " B";
              } else if (Math.abs(+value) >= 1000000) {
                result =
                  (+value / 1000000).toFixed(1).replace(/\.0$/, "") + " M";
              } else if (Math.abs(+value) >= 1000) {
                result = (+value / 1000).toFixed(1).replace(/\.0$/, "") + " K";
              } else {
                result = value;
              }
              if (toolTip === "VOLUME" || toolTip === "SKU") {
                return result;
              } else {
                return "$" + result;
              }
            },
          },
        },
      ],
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "Total",
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: newData.map((a: { start_point: any }) => a.start_point),
        },
        {
          name: toolTip,
          type: "bar",
          stack: "Total",
          label: {
            show: false,
            position: "top",
            formatter: function (d: any) {
              if (Math.abs(+d.data.value) >= 1000000000) {
                return (
                  (+d.data.value / 1000000000).toFixed().replace(/\.0$/, "") +
                  " B"
                );
              }
              if (Math.abs(+d.data.value) >= 1000000) {
                return (
                  (+d.data.value / 1000000).toFixed().replace(/\.0$/, "") + " M"
                );
              }
              if (Math.abs(+d.data.value) >= 1000) {
                return (
                  (+d.data.value / 1000).toFixed().replace(/\.0$/, "") + " K"
                );
              }
              return d.data.value;
            },
          },

          data: newData.map(
            (a: { add_points: any; label: any; value: any }) => {
              return {
                value: a.add_points,
                itemStyle: {
                  color: this.getFIGraphColor(a),
                },
              };
            },
          ),
        },
      ],
    };
    return option;
  }
  getFIGraphColor(key: any) {
    if (
      key.label === "Pre-Assortment" ||
      key.label === "Pre_assortment_total"
    ) {
      return "#C7972A";
    }
    if (
      key.label === "Post-Assortment" ||
      key.label === "Post_assortment_total"
    ) {
      return "#F7E6AB";
    }
    if (key.value < 0) {
      return "red";
    }
    if (
      key.label !== "Post-Assortment" &&
      key.label !== "Post_assortment_total" &&
      key.label !== "Pre-Assortment" &&
      key.label !== "Pre_assortment_total"
    ) {
      return "#E5B629";
    }
    return "#EED065";
  }

  setStackChartData(
    containerName: any,
    categories: any,
    prePortfolioData: any[] = [],
    modelOutputData: any[] = [],
    postPortfolioData: any[] = [],
  ) {
    const colors = [
      "#E8D2A0",
      "#ED8269",
      "#DC5F6D",
      "#985D79",
      "#4D5175",
      "#E9C699",
      "#EA766C",
      "#C46373",
      "#8B5A7A",
      "#3E5172",
      "#EAB791",
      "#E76C6E",
      "#B06478",
      "#7F577B",
      "#2B4A6B",
      "#EBA888",
      "#E6696E",
      "#A66178",
      "#75547C",
      "#194163",
      "#ED9A7B",
      "#E3646D",
      "#A06079",
      "#605278",
      "#06365A",
    ];
    const prePortfolioValue: { name: string; data: number[]; color: string }[] =
      [];
    const modelOutputValue: { name: string; data: number[]; color: string }[] =
      [];

    const postPortfolioValue: {
      name: string;
      data: number[];
      color: string;
    }[] = [];

    prePortfolioData.forEach((item: any, index: any) => {
      const newItem = { ...item };
      newItem.type = "bar";
      newItem.itemStyle = {
        borderColor: "transparent",
        color: colors[index % colors.length],
      };
      prePortfolioValue.push(newItem);
    });

    modelOutputData.forEach((item: any, index: any) => {
      const newItem = { ...item };
      newItem.type = "bar";
      newItem.itemStyle = {
        borderColor: "transparent",
        color: colors[index % colors.length],
      };
      modelOutputValue.push(newItem);
    });

    postPortfolioData.forEach((item: any, index: any) => {
      const newItem = { ...item };
      newItem.type = "bar";
      newItem.itemStyle = {
        borderColor: "transparent",
        color: colors[index % colors.length],
      };
      postPortfolioValue.push(newItem);
    });

    let chartData, actualData: any;
    if (
      containerName === "stack-preRecommendation" ||
      containerName === "stack-preRecommendationWithFI"
    ) {
      actualData = clone(prePortfolioValue);
      chartData = this.normalizeData(prePortfolioValue);
    } else if (
      containerName === "model-recommendation" ||
      containerName === "model-recommendationWithFI"
    ) {
      actualData = clone(modelOutputValue);
      chartData = this.normalizeData(modelOutputValue);
    } else if (
      containerName === "stack-postRecommendation" ||
      containerName === "stack-postRecommendationWithFI"
    ) {
      actualData = clone(postPortfolioValue);
      chartData = this.normalizeData(postPortfolioValue);
    } else {
      return;
    }

    // Initialize the chart instance
    const chartDom = document.getElementById(containerName)!;

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(chartDom);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }
    // Configure the chart options
    const option = {
      title: {
        text: "", // Empty title, as per Highcharts config
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow", // highlights the entire vertical bar
        },
        formatter: (params: any) => {
          let points = "";
          const paramIndex = categories.findIndex(
            (item: any) => item === params[0].name,
          );
          points += params[0].name + " <br/><br/>";
          params.forEach((each: any, index: any) => {
            points +=
              each.seriesName +
              " : <b>" +
              this.numberFormat(
                actualData[index].data[paramIndex],
                0,
                "",
                ",",
              ) +
              "</b> (" +
              each.value.toFixed(0) +
              "%)<br/>";
          });
          return points;
        },
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
        },
      },
      legend: {
        show: false, // Hide legend
      },
      xAxis: {
        type: "category",
        data: categories, // Categories (same as xAxis.categories in Highcharts)
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
          interval: 0,
        },
        axisLine: {
          lineStyle: {
            color: "#EFEFEF",
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: {
          show: false, // This hides the labels
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: "#EFEFEF",
          },
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      series: chartData.map((series: any) => ({
        ...series,
        type: "bar", // Column (bar) type
        barWidth: 50,
        stack: "total", // Stack columns (like stacking in Highcharts)
        label: {
          show: true,
          position: "inside", // Positioning labels inside bars
          formatter: function (params: any) {
            // If the value is less than 55, hide the label
            if (params.value < 5) {
              return "";
            }
            // Otherwise, show the label with a percentage sign
            return `${params.value.toFixed(0)}%`;
          },
          color: "#323232",
          fontSize: 12,
          fontWeight: 500,
          fontFamily: "Open Sans",
        },
        emphasis: {
          focus: "none",
        },
      })),
      // Customizing data label style and tooltip, following the Highcharts version
      plotOptions: {
        bar: {
          dataLabels: {
            enabled: true,
            formatter: "{point.percentage:.0f}%", // Format data label as percentage
          },
        },
      },
    };
    // Set the option for the chart
    option && chart.setOption(option);

    this.registerChart(chart);
  }
  normalizeData(data: any) {
    // Calculate the sum of each category
    const categoryCount = data[0]?.data.length; // number of categories (Total SKUs, Volume, etc.)
    const categorySums = new Array(categoryCount).fill(0);

    // Iterate over each series
    data.forEach((series: any) => {
      // Iterate over each category in the series
      series.data.forEach((value: any, index: any) => {
        categorySums[index] += value; // Add the value of the current category to the sum
      });
    });

    // Normalize the data to percentages
    const normalizedData = data.map((series: any) => {
      return {
        ...series,
        data: series.data.map(
          (value: any, index: any) => (value / categorySums[index]) * 100,
        ),
      };
    });
    return normalizedData;
  }
  setStackedWaterfallChartData(containerId: any, seriesData: any) {
    // @ts-ignore
    Highcharts.chart(containerId, {
      // @ts-ignore
      chart: {
        type: "waterfall",
      },
      title: {
        text: "",
      },
      xAxis: [
        {
          categories: seriesData.labels,
          labels: {
            style: {
              color: "#666666",
              fontSize: "14px",
              fontFamily: "Open Sans",
            },
          },
          lineColor: "#EFEFEF",
          lineWidth: 1,
          axisLabel: {
            color: "#666666",
            fontSize: 14,
            fontFamily: "Open Sans",
            rotate: 45, // Rotate the labels to make space for long labels
            interval: 0, // Display every label
          },
        },
      ],
      yAxis: {
        min: 0,
        title: {
          text: "",
        },
        labels: {
          enabled: false,
          format: "{value}%",
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
          },
        },
        gridLineWidth: 1,
        gridLineColor: "#EFEFEF",
      },
      plotOptions: {
        series: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 0,
        },
      },
      tooltip: {
        backgroundColor: "#000000",
        style: {
          color: "#ffffff",
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      series: seriesData.data,
    });
  }

  setGroupedBarChartData(containerName: any, labels: any, barData: any) {
    // Convert labels to title case
    const updatedLabels = labels.map((label: any) => this.toTitleCase(label));

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(
      document.getElementById(containerName)!,
    );

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(document.getElementById(containerName)!);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }

    // Configure the chart options
    const echartSeries = barData.map((item: any) => {
      return {
        name: item.name,
        type: "bar",
        barGap: 0,
        itemStyle: {
          borderColor: "transparent",
          color: item.color,
        },
        data: item.data,
      };
    });
    const option = {
      title: {
        text: "",
        left: "left",
      },
      legend: {
        data: barData.map((data: any) => data.name),
        show: false,
      },
      tooltip: {
        trigger: "item", // Show tooltip for each individual item (bar)
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
        },
        formatter: function (params: any) {
          // The tooltip shows the series name and the value in millions
          return `<b>${params.seriesName}</b>: ${params.value.toFixed(2)}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: updatedLabels,
        axisLine: {
          lineStyle: {
            color: "#EFEFEF",
          },
        },
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
          interval: 0, // Display every label
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        axisLine: {
          show: true, // Show the Y-axis line
          lineStyle: {
            width: 1, // Y-axis line width (optional)
            type: "solid", // Y-axis line style (optional)
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
          formatter: function (value: number) {
            // Format the Y-axis value as millions
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            }
            return value;
          },
        },
      },
      series: echartSeries,
    };

    // Set the option for the chart
    option && chart.setOption(option);
    this.registerChart(chart);
  }

  setWaterfallChartData(
    containerName: string,
    data?: any,
    data1?: any,
    data2?: any,
    topCount: number = 0,
    bottomCount: number = 0,
  ) {
    const { chartData, minValue, markAreas, markAreas1 } = this.selectChartData(
      containerName,
      data,
      data1,
      data2,
      topCount,
      bottomCount,
    );

    if (!chartData) return;

    const { placeholderSeries, copiedData, maxValue } =
      this.computePlaceholderAndMax(chartData, containerName, topCount);

    // Initialize or clear chart instance
    const chart = this.getOrCreateChart(containerName);
    const option = {
      title: {
        text: "",
      },
      grid: {
        left: containerName === "macoAnalysisChart" ? "5%" : "20%", // Adjust this value to shrink the chart area and add space for labels
        right: "3%",
        top: "10%",
        bottom: "10%",
      },
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          const colorDot = `<span style="color: ${params.color}">&#9679;</span>`;
          return `<b>${colorDot} ${params.name} : ${this.numberFormat(params.value, 0, "", ",")}</b>`;
        },
        fontWeight: 500,
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
        },
      },
      xAxis: {
        type: "category",
        data: chartData.map((item: any) => item.name),
        axisLine: {
          lineStyle: {
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          show: true,
          rotate: 0,
          interval: 0, // Display all labels
          formatter: function (value: string) {
            return value.replace(/ /g, "\n"); // Split after spaces for line breaks
          },
          textStyle: {
            color: "#666666", // White axis labels
          },
          fontSize: 12, // Font size for labels
          lineHeight: 16, // Adjust the line height to prevent overlap
        },
      },
      yAxis: {
        type: "value",
        min: minValue > 1000000 ? minValue - 1000000 : minValue,
        max: maxValue,
        axisLine: {
          show: true,
          lineStyle: {
            color: "#EFEFEF",
          },
        },
        axisLabel: {
          show: true,
          // rotate: 45, // Rotate labels by 45 degrees
          margin: 20, // Add more space between yAxis labels and chart area
          interval: 0,
          textStyle: {
            color: "#666666", // White axis labels
          },
          formatter: (value: number) => {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + "K";
            }
            return value;
          },
        },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "waterfall",
          barWidth: function (params: any) {
            // If the value is 0, reduce the bar width
            return params.name === "" ? 1 : 20; // Set a small width for 0 values (2px), otherwise 30px
          }, // Set the width of the bars
          barGap: "0%",
          silent: true,
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: placeholderSeries,
          markArea: markAreas1,
        },
        {
          name: "Waterfall",
          type: "bar",
          stack: "waterfall",
          data: chartData.map((item: any) => {
            return {
              value: Math.abs(item.y),
              itemStyle: {
                color: item.color,
              },
            };
          }),
          markArea: markAreas,
          barMinHeight: 0, // Adjust this value as needed
          barWidth: function (params: any) {
            // If the value is 0, reduce the bar width
            return params.name === "" ? 1 : 20; // Set a small width for 0 values (2px), otherwise 30px
          }, // Set the width of the bars
          barGap: "0%",
          label: {
            show: true, // Show the labels
            position: "top", // Position the label inside the bar
            color: "#000", // Label color (optional)
            fontSize: 12, // Label font size (optional)
            fontWeight: 500,
            formatter: (params: any) => {
              const data = chartData[params?.dataIndex].y;
              if (Math.abs(data) >= 1000000) {
                return (data / 1000000).toFixed(1) + "M";
              } else if (Math.abs(data) >= 1000) {
                return (data / 1000).toFixed(1) + "K";
              } else if (data === 0) {
                return "";
              }
              return data.toFixed(1);
            },
          },
        },
      ],
    };
    option && chart.setOption(option);
    chart.resize();
    this.registerChart(chart);
  }

  private calculateMinValue(data: any, preName: string, upliftName: string) {
    const pre = data?.find((d: any) => d.name === preName)?.y;
    const negativeUplift = data?.find((d: any) => d.name === upliftName)?.y;
    const percentageNegativeUplift = (negativeUplift / pre) * 100;
    const adjustedPercentage = percentageNegativeUplift + 10;
    const finalValue = (adjustedPercentage / 100) * pre;
    return finalValue * 10;
  }

  private calculateSecondMaxYValue(data2: any) {
    const yValues = data2.map((item: any) => item.y);
    const uniqueSortedYValues = [...new Set(yValues)].sort(
      (a: any, b: any) => b - a,
    );
    return uniqueSortedYValues[1];
  }

  private selectChartData(
    containerName: string,
    data: any,
    data1: any,
    data2: any,
    topCount: number,
    bottomCount: number,
  ) {
    const name = containerName || "";
    const chartData: any = this.getDataSource(name, data, data1, data2);
    if (!chartData)
      return {
        chartData: null,
        minValue: null,
        markAreas: null,
        markAreas1: null,
      };

    const minValue: any = this.computeMinValueFor(name, data, data1, data2);

    // special handling for macoAnalysis chart
    if (name === "macoAnalysisChart") {
      const result = this.handleMacoAnalysis(chartData, topCount, bottomCount);
      return {
        chartData: result.chartData,
        minValue,
        markAreas: result.markAreas,
        markAreas1: result.markAreas1,
      };
    }

    return { chartData, minValue, markAreas: {}, markAreas1: {} };
  }

  private getDataSource(name: string, data: any, data1: any, data2: any) {
    const isMaco =
      name === "macoUpliftChart" || name.includes("macoUpliftChart");
    const isVolume =
      name === "volumeUpliftChart" || name.includes("volumeUpliftChart");
    const isMacoAnalysis = name === "macoAnalysisChart";
    const isRevenue = name.includes("revenueUpliftChart");

    if (isMaco) return data;
    if (isVolume) return data1;
    if (isMacoAnalysis || isRevenue) return data2;
    return null;
  }

  private computeMinValueFor(name: string, data: any, data1: any, data2: any) {
    const isMaco =
      name === "macoUpliftChart" || name.includes("macoUpliftChart");
    const isVolume =
      name === "volumeUpliftChart" || name.includes("volumeUpliftChart");
    const isMacoAnalysis = name === "macoAnalysisChart";
    const isRevenue = name.includes("revenueUpliftChart");

    if (isMaco && data)
      return this.calculateMinValue(data, "Pre", "Negative Uplift");
    if (isVolume && data1)
      return this.calculateMinValue(data1, "Pre", "Volume Delisted");
    if (isMacoAnalysis && data2) return this.calculateSecondMaxYValue(data2);
    if (isRevenue && data2)
      return this.calculateMinValue(data2, "Pre", "Negative Uplift");
    return undefined;
  }

  private handleMacoAnalysis(
    chartData: any,
    topCount: number,
    bottomCount: number,
  ) {
    if (!chartData)
      return { chartData: null, markAreas: null, markAreas1: null };

    const safeName = (idx: number) =>
      (chartData[idx] && chartData[idx].name) || "";

    let markAreas: any = {};
    let markAreas1: any = {};

    if (topCount > 0) {
      markAreas = {
        data: [
          [
            { name: "Top Positive Uplift", xAxis: safeName(1) },
            { xAxis: safeName(topCount) },
          ],
        ],
        itemStyle: {
          color: "rgba(76, 182, 76, 0.04)",
          borderColor: "#EFEFEF",
          borderWidth: 2,
          borderType: "dotted",
        },
        tooltip: { show: false },
        label: {
          show: true,
          position: "top",
          formatter: "Top Positive Uplift",
          fontWeight: 600,
          fontSize: 14,
          distance: 20,
          color: "#666666",
        },
      };
    }
    chartData.splice(topCount + 1, 0, { name: "", y: 0 });
    if (bottomCount > 0) {
      markAreas1 = {
        data: [
          [
            { name: "Botton Negative Uplift", xAxis: safeName(topCount + 2) },
            { xAxis: safeName(topCount + 2 + bottomCount) },
          ],
        ],
        itemStyle: {
          color: "rgba(230, 120, 120, 0.04)",
          borderColor: "#EFEFEF",
          borderWidth: 2,
          borderType: "dotted",
        },
        tooltip: { show: false },
        label: {
          show: true,
          position: "top",
          formatter: "Bottom Negative Uplift",
          fontWeight: 600,
          fontSize: 14,
          distance: 20,
          color: "#666666",
        },
      };
    }

    return { chartData, markAreas, markAreas1 };
  }

  private computePlaceholderAndMax(
    chartData: any[],
    containerName: string,
    topCount: number,
  ) {
    const placeholderSeries = [0];
    const copiedData = clone(chartData);
    copiedData?.forEach((item: any, index: any) => {
      if (!item.isIntermediateSum && !item.isSum) {
        placeholderSeries.push(item?.y + placeholderSeries[index]);
        if (item?.y < 0) {
          placeholderSeries[index] = placeholderSeries[index] + item?.y;
          item.y = Math.abs(item?.y);
        }
      }

      if (item.isIntermediateSum || item.isSum) {
        placeholderSeries[index] = 0;
        item.y = 0;
        copiedData.forEach((element: any, cindex: any) => {
          if (cindex < index && !element.isIntermediateSum) {
            item.y += chartData[cindex]?.y;
          }
        });
        placeholderSeries.push(item?.y);
      }
    });
    placeholderSeries.pop();
    const maxHeight = Math.max(...placeholderSeries.map(Number));
    const maxIndex = placeholderSeries.indexOf(maxHeight);
    let maxValue = maxHeight + copiedData[maxIndex]?.y;

    if (containerName === "macoAnalysisChart") {
      placeholderSeries.splice(topCount + 1, 1, 0);
      maxValue = Math.floor(maxValue / 100000 + 1) * 100000;
    } else {
      maxValue = Math.floor(maxValue / 1000000 + 1) * 1000000;
    }

    return { placeholderSeries, copiedData, maxValue };
  }

  private getOrCreateChart(containerName: string) {
    const chartDom = document.getElementById(containerName)!;
    let chart = echarts.getInstanceByDom(chartDom);
    if (!chart) chart = echarts.init(chartDom);
    else chart.clear();
    return chart;
  }

  setBarChartData(containerName: any, demandTransferData: any) {
    //@ts-ignore
    Highcharts.chart(containerName, {
      //@ts-ignore
      chart: {
        type: "column",
      },
      title: {
        text: "",
        align: "left",
      },
      xAxis: {
        categories: demandTransferData.labels,
        crosshair: false,
        labels: {
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
          },
        },
        lineColor: "#EFEFEF",
        lineWidth: 1,
      },
      yAxis: {
        min: 0,
        max: demandTransferData?.maxYaxisValue,
        title: {
          text: "",
        },
        labels: {
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
          },
        },
        gridLineWidth: 1,
        gridLineColor: "#EFEFEF",
      },
      tooltip: {
        valueSuffix: "",
        backgroundColor: "#000000",
        style: {
          color: "#ffffff",
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.05,
          groupPadding: 0,
          borderWidth: 0,
          grouping: false,
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      series: demandTransferData.datasets,
    });
  }

  getJourneyChartData(data: any, container: any) {
    data = data.map((item: any) =>
      item.name === "Delist Pipeline SKUs"
        ? { ...item, name: "Delist Pipeline Executed" }
        : item,
    );

    // Set intermediate sum and sum points
    data?.forEach((item: any, index: number, array: any[]) => {
      item.isIntermediateSum = index === 2;
      item.isSum = index === array.length - 1;
    });

    const primaryData = data.map((item: any) => ({
      name: item.name,
      y: item.y ?? 0,
      color: "#D69E77",
      isSum: item.isSum,
      isIntermediateSum: item.isIntermediateSum,
    }));

    if (primaryData[3]) primaryData[3].color = "#959B7B";
    if (primaryData[4]) primaryData[4].color = "#959B7B";

    const secondaryData = data.map((item: any) => ({
      name: item.name,
      y: item.y1 ?? 0,
      color: "#325A6D",
      isSum: item.isSum,
      isIntermediateSum: item.isIntermediateSum,
    }));

    //@ts-ignore
    Highcharts.chart(container, {
      //@ts-ignore
      chart: { type: "waterfall" },
      title: { text: "" },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: "#666666",
            fontSize: "14px",
            fontFamily: "Open Sans",
            whiteSpace: "normal",
            textOverflow: "none",
          },
          rotation: 0,
          autoRotation: false,
          overflow: "allow",
          useHTML: true,
        },
        minPadding: 0,
        maxPadding: 0,
        lineColor: "#EFEFEF",
        lineWidth: 1,
      },
      yAxis: {
        title: { text: "" },
        labels: {
          style: {
            color: "#666666",
            fontSize: "12px",
            fontFamily: "Open Sans",
          },
        },
        lineColor: "#EFEFEF",
        lineWidth: 1,
      },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: {
        formatter: function () {
          if (!this.points) return "";

          const primaryPoint = this.points.find(
            (p) => p.series.name === "Primary",
          );
          const secondaryPoint = this.points.find(
            (p) => p.series.name === "Secondary",
          );

          const primaryDot = primaryPoint
            ? `<span style="color: ${primaryPoint.color}">&#9679;</span>`
            : "";
          const secondaryDot = secondaryPoint
            ? `<span style="color: ${secondaryPoint.color}">&#9679;</span>`
            : "";

          const primaryText =
            primaryPoint && typeof primaryPoint.y === "number"
              ? `${primaryDot} Active SKUs: ${Highcharts.numberFormat(primaryPoint.y, 0, "", ",")}`
              : "";

          const secondaryText =
            secondaryPoint && typeof secondaryPoint.y === "number"
              ? ` ${secondaryDot} Delist Pipeline: ${Highcharts.numberFormat(secondaryPoint.y, 0, "", ",")}`
              : "";

          return `<b>${this.point.name}</b><br/>${primaryText}<br/>${secondaryText}`;
        },
        shared: true,
        backgroundColor: "#000000",
        style: { color: "#ffffff" },
      },
      plotOptions: {
        series: { stacking: "normal" }, // Enable stacking
      },
      series: [
        ...(secondaryData.some((item: any) => item.y !== 0) // Adding secondary series only if y1 values exist
          ? [
              {
                name: "Secondary", // Delist Pipeline (Bottom First)
                type: "waterfall",
                data: secondaryData,
                borderWidth: 0,
                borderRadius: 0,
                dataLabels: {
                  enabled: true,
                  inside: true,
                  verticalAlign: "middle",
                  align: "center",
                  y: 0,
                  crop: false,
                  overflow: "none",
                  style: {
                    color: "#000000",
                    fontSize: "12px",
                    fontFamily: "Open Sans",
                    fontWeight: 500,
                    textOutline: "none",
                  },
                  formatter: function () {
                    return this.y === 0 ? "" : this.y;
                  },
                },
              },
            ]
          : []),
        {
          name: "Primary", // Active SKUs (On Top)
          type: "waterfall",
          data: primaryData,
          borderWidth: 0,
          borderRadius: 0,
          dataLabels: {
            enabled: true,
            inside: true,
            verticalAlign: "middle",
            align: "center",
            y: 0,
            crop: false,
            overflow: "none",
            style: {
              color: "#000000",
              fontSize: "12px",
              fontFamily: "Open Sans",
              fontWeight: 500,
              textOutline: "none",
            },
            formatter: function () {
              return this.y === 0 ? "" : this.y;
            },
          },
        },
      ],
    });
  }

  setTailAnalysisChartData1(
    containerName: string,
    firstTailAnalysisChart: any[],
    secondTailAnalysisChart: any[],
    thirdTailAnalysisChart: any[],
    fourthTailAnalysisChart: any[],
  ) {
    let apiData: any, brands;

    if (containerName === "first-tail") {
      brands = firstTailAnalysisChart[0];
      apiData = firstTailAnalysisChart[1];
    } else if (containerName === "second-tail") {
      brands = secondTailAnalysisChart[0];
      apiData = secondTailAnalysisChart[1];
    } else if (containerName === "third-tail") {
      brands = thirdTailAnalysisChart[0];
      apiData = thirdTailAnalysisChart[1];
    } else if (containerName === "fourth-tail") {
      brands = fourthTailAnalysisChart[0];
      apiData = fourthTailAnalysisChart[1];
    }

    const chartData = this.normalizeData(clone(apiData));

    const entries = brands.length;
    const baseHeight = 450; // for 10 entries
    const chartHeight = Math.ceil(entries / 10) * baseHeight;

    // Initialize the chart instance
    const chartDom = document.getElementById(containerName)!;

    chartDom.style.height = chartHeight + "px";

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(chartDom);

    // Wait a bit to let DOM apply new height
    setTimeout(() => {
      chart?.resize();
    }, 50);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: function (params: any) {
          let str = `${params[0].name}<br/><br/>`;
          params.forEach((item: any, index: any) => {
            const percent = item.value.toFixed(2);
            const value = apiData[index].data[item.dataIndex];
            str += `${toTitleCase(item.seriesName)}: <b>${value}</b> (${percent}%)<br/>`;
          });
          return str;
        },
        backgroundColor: "#000000",
        textStyle: {
          color: "#ffffff",
          fontSize: 14,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "8%",
        top: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: "#EFEFEF" } },
        axisLabel: { show: false },
        splitLine: {
          lineStyle: { color: "#EFEFEF" },
        },
      },
      yAxis: {
        type: "category",
        data: brands,
        axisLine: { lineStyle: { color: "#EFEFEF" } },
        axisLabel: {
          color: "#666666",
          fontSize: 14,
          fontFamily: "Open Sans",
        },
        inverse: true,
      },
      series: chartData.map((series: any) => ({
        ...series,
        type: "bar",
        stack: "total",
        label: {
          show: true,
          position: "inside",
          formatter: (params: any) => {
            const p = params.data;
            if (p === 0 || p < 2) return "";
            return `${p.toFixed(1)}%`;
          },
          color: "#000000",
          fontSize: 14,
          fontFamily: "Open Sans",
          fontWeight: 500,
        },
        emphasis: {
          focus: "none",
        },
      })),
      legend: { show: false },
    };

    chart.setOption(option);
    window.addEventListener("resize", () => chart?.resize());
  }

  // stacked waterfall chart for executive summar
  getStackedJourneyChartData(data: any, container: any) {
    const apiData = clone(data);

    // Set intermediate sum and sum points
    data?.forEach((item: any, index: number, array: any[]) => {
      item.isIntermediateSum = index === 2;
      item.isSum = index === array.length - 1;
    });

    const placeholderSeries = [0];
    data?.forEach((item: any, index: any) => {
      if (!item.isIntermediateSum && !item.isSum) {
        placeholderSeries.push(item?.y + item?.y1 + placeholderSeries[index]);
        //for negative values
        if (item?.y + item?.y1 < 0) {
          placeholderSeries[index] =
            placeholderSeries[index] + (item?.y + item?.y1);
          if (item?.y < 0) {
            item.y = Math.abs(item?.y);
          }
          if (item?.y1 < 0) {
            item.y1 = Math.abs(item?.y1);
          }
        }
      }

      //intermediate sum and sum calculation
      if (item.isIntermediateSum || item.isSum) {
        placeholderSeries[index] = 0;

        item.y = 0;
        item.y1 = 0;
        data.forEach((element: any, cindex: any) => {
          if (cindex < index && !element.isIntermediateSum) {
            item.y += apiData[cindex]?.y;
            item.y1 += apiData[cindex]?.y1;
          }
        });
        placeholderSeries.push(item?.y + item?.y1);
      }
    });

    const categories = data.map((item: any) => item?.name);
    const seriesDataY = data.map((item: any) => Math.abs(item?.y));
    const seriesDataY1 = data.map((item: any) => Math.abs(item?.y1));

    // Removed SKU Count change
    placeholderSeries.splice(1, 1);
    seriesDataY.splice(1, 1);
    seriesDataY1.splice(1, 1);
    categories.splice(1, 1);

    const chartDom = document.getElementById(container);
    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(document.getElementById(container)!);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }
    const option = {
      title: {
        text: "",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params: any) {
          // Check if there are multiple series
          const primaryPoint = params.find(
            (p: any) => p.seriesName === "Active SKUs",
          );
          const secondaryPoint = params.find(
            (p: any) => p.seriesName === "Delist Pipeline",
          );

          const primaryDot = primaryPoint
            ? `<span style="color: ${primaryPoint.color}">&#9679;</span>`
            : "";
          const secondaryDot = secondaryPoint
            ? `<span style="color: ${secondaryPoint.color}">&#9679;</span>`
            : "";

          const primaryText =
            primaryPoint && primaryPoint.value !== null
              ? `${primaryDot} Active SKUs: ${Math.abs(primaryPoint.value).toLocaleString()}`
              : "";

          const secondaryText =
            secondaryPoint && secondaryPoint.value !== null
              ? ` ${secondaryDot} Delist Pipeline: ${Math.abs(secondaryPoint.value).toLocaleString()}`
              : "";
          if (
            params[0].name === "New innovation SKUs" ||
            params[0].name === "New non-innovation SKUs" ||
            params[0].name === "Delist Pipeline SKUs" ||
            params[0].name === "Delisted SKUs"
          ) {
            return `<b>${params[0].name} : ${Math.abs(primaryPoint.value !== 0 ? primaryPoint.value : secondaryPoint.value).toLocaleString()}</b>`;
          }
          return `<b>${params[0].name}</b><br/>${primaryText}<br/>${secondaryText}`;
        },
        backgroundColor: "#000000",
        textStyle: { color: "#ffffff" },
        shared: true,
      },
      legend: {
        show: false, // Hides the legend
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLine: {
          lineStyle: {
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          show: true,
          interval: 0, // Display all labels
          formatter: function (value: any) {
            return value.replaceAll(" ", "\n"); // Join them with newline for wrapping
          },
          textStyle: {
            color: "#666666", // White axis labels
          },
          fontSize: 12, // Font size for labels
          lineHeight: 16, // Adjust the line height to prevent overlap
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true, // Show the Y-axis line
          lineStyle: {
            width: 1, // Y-axis line width (optional)
            type: "solid", // Y-axis line style (optional)
            color: "#EFEFEF", // White axis line
          },
        },
        axisLabel: {
          textStyle: {
            color: "#666666", // White axis labels
          },
        },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "Total",
          barWidth: 25, // Set the width of the bars
          barGap: "50%",
          silent: true,
          itemStyle: {
            borderColor: "transparent",
            color: "transparent",
          },
          emphasis: {
            itemStyle: {
              borderColor: "transparent",
              color: "transparent",
            },
          },
          data: placeholderSeries,
        },
        {
          name: "Active SKUs",
          type: "bar",
          stack: "Total",
          barWidth: 25, // Set the width of the bars
          barGap: "50%",
          label: {
            show: true, // Show the labels
            position: "inside", // Position the label inside the bar
            verticalAlign: "middle", // Vertically align the label to the middle of the bar
            align: "center", // Horizontally align the label to the center of the bar
            color: "#000", // Label color (optional)
            fontSize: 12, // Label font size (optional)
            fontWeight: 500,
            formatter: function (params: any) {
              return params.value === 0 ? "" : Math.abs(params.value); // Hide label if value is 0
            },
          },
          itemStyle: {
            borderColor: "transparent",
            color: "#D69E77",
          },
          data: seriesDataY,
        },
        {
          name: "Delist Pipeline",
          type: "bar",
          stack: "Total",
          barWidth: 25, // Set the width of the bars
          barGap: "50%",
          label: {
            show: true, // Show the labels
            position: "inside", // Position the label inside the bar
            verticalAlign: "middle", // Vertically align the label to the middle of the bar
            align: "center", // Horizontally align the label to the center of the bar
            color: "#000", // Label color (optional)
            fontSize: 12, // Label font size (optional)
            fontWeight: 500,
            formatter: function (params: any) {
              return params.value === 0 ? "" : Math.abs(params.value);
            },
          },
          itemStyle: {
            borderColor: "transparent",
            color: "#325A6D",
          },
          data: seriesDataY1,
        },
      ],
    };
    option && chart.setOption(option);
    // Ensure the chart resizes with the window
    this.registerChart(chart);
  }

  registerChart(chart: echarts.ECharts) {
    this.registeredCharts.push(chart);
    this.addResizeListener(); // only adds once
  }

  private addResizeListener() {
    if (this.resizeListenerAdded) return;

    const debounce = (func: () => void, wait: number) => {
      let timeout: any;
      return () => {
        clearTimeout(timeout);
        timeout = setTimeout(func, wait);
      };
    };

    const onResize = debounce(() => {
      this.registeredCharts.forEach((chart) => {
        chart.resize();
      });
    }, 300);

    window.addEventListener("resize", onResize);
    this.resizeListenerAdded = true;
  }

  numberFormat(
    value: any,
    decimals = 0,
    decimalPoint = ".",
    thousandsSep = ",",
  ) {
    // Convert value to number if it's a string
    value = Number(value);

    // If the value is NaN, return it as is
    if (isNaN(value)) {
      return value;
    }

    // Round the value to the specified decimal places
    const fixedValue = value.toFixed(decimals);

    // Split the value into the integer and decimal parts
    let [integerPart, decimalPart] = fixedValue.split(".");

    // Add thousands separators to the integer part
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);

    // If there are decimals, join the integer and decimal parts, else just return the integer part
    if (decimals > 0) {
      return `${integerPart}${decimalPoint}${decimalPart}`;
    } else {
      return integerPart;
    }
  }
  getDashboardStackedJourneyChartData(data: any, container: string) {
    const colors = [
      "#E8D2A0",
      "#ED8269",
      "#DC5F6D",
      "#985D79",
      "#4D5175",
      "#E9C699",
      "#EA766C",
      "#C46373",
      "#8B5A7A",
      "#3E5172",
      "#EAB791",
      "#E76C6E",
      "#B06478",
      "#7F577B",
      "#2B4A6B",
      "#EBA888",
      "#E6696E",
      "#A66178",
      "#75547C",
      "#194163",
      "#ED9A7B",
      "#E3646D",
      "#A06079",
      "#605278",
      "#06365A",
    ];

    const keySet = Object.keys(data);
    const placeholderSeries = new Array(data[keySet[0]].length).fill(0);

    placeholderSeries.forEach((item: any, index: any, arr: any) => {
      let sum1 = 0,
        sum2 = 0;
      if (index !== 0 && index !== 2 && index !== arr.length - 1) {
        keySet.forEach((key: any) => {
          sum1 += data[key][index - 1].y;
          sum2 += data[key][index].y;
        });
        placeholderSeries[index] =
          placeholderSeries[index - 1] +
          (sum1 < 0 ? 0 : sum1) +
          (sum2 < 0 ? sum2 : 0);
      }

      if (index === 2 || index === arr.length - 1) {
        placeholderSeries[index] = 0;
        keySet.forEach((key: any) => {
          const total = data[key]
            .slice(0, index)
            .filter((_: any, i: any) => i !== 2)
            .reduce((acc: number, val: any) => acc + val.y, 0);
          data[key][index].y = total;
        });
      }
    });

    // 👇️ Remove index 1 from data and placeholderSeries
    const removeIndex = 1;
    keySet.forEach((key) => {
      data[key].splice(removeIndex, 1);
    });
    placeholderSeries.splice(removeIndex, 1);

    const categories = data[keySet[0]].map((item: any) => item.name);

    const series = keySet.map((group, index) => ({
      name: group,
      type: "bar",
      stack: "total",
      data: data[group].map((item: any) => Math.abs(item.y)),
      barWidth: 40,
      barGap: "50%",
      label: {
        show: true,
        position: "inside",
        verticalAlign: "middle",
        align: "center",
        color: "#000",
        fontSize: 12,
        fontWeight: 500,
        formatter: (params: any) => (params.value === 0 ? "" : params.value),
      },
      itemStyle: {
        borderColor: "transparent",
        color: colors[index % colors.length],
      },
    }));

    const chartDom = document.getElementById(container);

    // Check if the chart instance already exists
    let chart = echarts.getInstanceByDom(document.getElementById(container)!);

    if (!chart) {
      // If chart instance doesn't exist, initialize it
      chart = echarts.init(chartDom);
    } else {
      // If the chart instance already exists, clear it
      chart.clear();
    }

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          let tooltip = `<b>${params[0].name}</b>`;
          params.forEach((item: any) => {
            if (item.seriesName !== "Placeholder") {
              tooltip += `<br><span style="color:${item.color}">&#9679;</span> ${item.seriesName}: ${Math.abs(item.value).toLocaleString()}`;
            }
          });
          return tooltip;
        },
        backgroundColor: "#000",
        textStyle: { color: "#fff" },
        shared: true,
      },
      legend: { show: false },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLine: { lineStyle: { color: "#EFEFEF" } },
        axisLabel: {
          show: true,
          interval: 0,
          formatter: (value: string) => value.replace(/ /g, "\n"),
          textStyle: {
            color: "#666",
            fontSize: 12,
            lineHeight: 16,
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true,
          lineStyle: {
            width: 1,
            type: "solid",
            color: "#EFEFEF",
          },
        },
        axisLabel: { textStyle: { color: "#666" } },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "total",
          barWidth: 40,
          barGap: "50%",
          silent: true,
          itemStyle: {
            color: "transparent",
            borderColor: "transparent",
          },
          emphasis: {
            itemStyle: {
              color: "transparent",
              borderColor: "transparent",
            },
          },
          data: placeholderSeries,
        },
        ...series,
      ],
    };

    chart.setOption(option);
    this.registerChart(chart);
  }

  createDtpChart(containerName: string, data: any[], yAxisLabel: string) {
    const chartDom = document.getElementById(containerName);
    if (!chartDom) return;

    let chart = echarts.getInstanceByDom(chartDom);
    if (!chart) {
      chart = echarts.init(chartDom);
    } else {
      chart.clear();
    }

    const maxValue = Math.max(...data.map((d) => d.value));
    const calculatedMax = Math.ceil((maxValue * 1.2) / 50) * 50;
    const dynamicInterval =
      calculatedMax <= 100
        ? 10
        : calculatedMax <= 500
          ? 50
          : calculatedMax <= 1000
            ? 100
            : 200;

    const option: echarts.EChartsOption = {
      grid: {
        left: "10%",
        right: "10%",
        top: "5%",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        show: true,
        max: calculatedMax,
        min: 0,
        interval: dynamicInterval,
        axisLabel: {
          show: true,
          fontSize: 12,
          color: "#666",
          formatter: "{value}",
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: "#C8C8C8",
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: "category",
        data: data.map((d) => d.name),
        position: "left",
        axisLabel: {
          show: false,
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: "#C8C8C8",
          },
        },
        axisTick: {
          show: false,
        },
      },
      series: [
        {
          type: "bar",
          data: data.map((d) => ({
            value: d.value,
            itemStyle: {
              color: d.color,
            },
          })),
          barWidth: data.length === 2 ? 40 : data.length === 3 ? 30 : 25,
          label: {
            show: true,
            position: "right",
            formatter: (params: any) => {
              const item = data[params.dataIndex];
              if (item.change !== undefined) {
                const changeColor = item.changeColor || "##000000";
                return `{value|${params.value}} {change${params.dataIndex}|(${item.change}%)}`;
              }
              return `{value|${params.value}}`;
            },
            rich: (() => {
              const richConfig: any = {
                value: {
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#000",
                  padding: [0, 4, 0, 10],
                },
              };
              data.forEach((item, index) => {
                if (item.change !== undefined) {
                  richConfig[`change${index}`] = {
                    fontSize: 10,
                    fontWeight: 600,
                    color: item.changeColor || "#000000",
                  };
                }
              });
              return richConfig;
            })(),
          },
        },
      ],
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          const item = data[params.dataIndex];
          return `<b>${item.name}</b><br/>${params.value}`;
        },
      },
      graphic: [
        {
          type: "text",
          left: "5%",
          top: "center",
          style: {
            text: yAxisLabel,
            fontSize: 14,
            fontWeight: 600,
            fill: "#666666",
          },
          rotation: Math.PI / 2,
        },
      ],
    };

    chart.setOption(option);
    this.registerChart(chart);
  }

  initPhiBridgeChart(
    chartDomId: string,
    phiBridgeData: any,
  ): echarts.ECharts | null {
    const chartDom = document.getElementById(chartDomId);
    if (!chartDom) return null;

    let chart = echarts.getInstanceByDom(chartDom);
    if (!chart) {
      chart = echarts.init(chartDom);
    } else {
      chart.clear();
    }

    const { categories, totals, isTotal, percentChange, seriesData } =
      phiBridgeData;

    const hasValues = (vals: number[]) =>
      Array.isArray(vals) && vals.some((v: number) => v !== 0);

    const categoryConfig: { name: string; color: string; values: number[] }[] =
      (seriesData || []).filter((s: any) => hasValues(s.values));

    const placeholderValues: number[] = [];
    placeholderValues.push(0);
    placeholderValues.push(0);
    placeholderValues.push(totals[1]);
    placeholderValues.push(totals[4]);
    placeholderValues.push(0);

    const barWidth = 40;
    const markLineData: any[] = [];

    const labelDec24 = categories[1] ?? "Dec 24";
    const labelNew25 = categories[2] ?? "New 25";
    const labelDelist25 = categories[3] ?? "Delist 25";
    const labelDec25 = categories[4] ?? "Dec 25";

    markLineData.push([
      { xAxis: labelDec24, yAxis: totals[1] },
      { xAxis: labelNew25, yAxis: totals[1] },
    ]);

    markLineData.push([
      { xAxis: labelNew25, yAxis: totals[1] + totals[2] },
      { xAxis: labelDelist25, yAxis: totals[4] + totals[3] },
    ]);

    markLineData.push([
      { xAxis: labelDelist25, yAxis: totals[4] },
      { xAxis: labelDec25, yAxis: totals[4] },
    ]);

    const maxBarHeight = Math.max(
      totals[0],
      totals[1],
      totals[1] + totals[2],
      totals[4] + totals[3],
      totals[4],
    );
    const yAxisMax = Math.ceil((maxBarHeight * 1.2) / 50) * 50;

    const dynamicInterval = (() => {
      if (yAxisMax <= 100) return 10;
      if (yAxisMax <= 500) return 50;
      if (yAxisMax <= 1000) return 100;
      if (yAxisMax <= 5000) return 250;
      return Math.ceil(yAxisMax / 10);
    })();

    const categorySeries: any[] = categoryConfig.map((cat, idx) => {
      const isLast = idx === categoryConfig.length - 1;
      const series: any = {
        name: cat.name,
        type: "bar",
        stack: "bridge",
        barWidth: 40,
        itemStyle: { color: cat.color },
        label: {
          show: true,
          position: "inside",
          color: "#000000",
          fontSize: 9.6,
          fontWeight: 400,
          fontFamily: "Work Sans",
          formatter: (params: any) => {
            const val = cat.values[params.dataIndex];
            return val !== 0 ? String(val) : "";
          },
        },
        data: cat.values,
      };
      if (isLast) {
        series.markLine = {
          silent: true,
          symbol: "none",
          lineStyle: { type: "dashed", color: "#666666", width: 1 },
          label: { show: false },
          data: markLineData,
        };
      }
      return series;
    });

    const option: echarts.EChartsOption = {
      grid: {
        left: 60,
        right: 20,
        top: 60,
        bottom: 40,
        containLabel: false,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#E2E7EA" } },
        axisLabel: {
          color: "#666666",
          fontSize: 11.2,
          fontFamily: "Work Sans",
          fontWeight: 400,
        },
      },
      yAxis: {
        type: "value",
        max: yAxisMax,
        interval: dynamicInterval,
        splitLine: { show: false },
        axisLine: { show: true, lineStyle: { color: "#E2E7EA", width: 1 } },
        axisLabel: {
          color: "#666666",
          fontSize: 11.2,
          fontFamily: "Work Sans",
          fontWeight: 400,
        },
        axisTick: { show: false },
      },
      series: [
        {
          name: "Placeholder",
          type: "bar",
          stack: "bridge",
          barWidth: 40,
          itemStyle: {
            color: "transparent",
            borderColor: "transparent",
          },
          emphasis: {
            itemStyle: {
              color: "transparent",
              borderColor: "transparent",
            },
          },
          data: placeholderValues,
          tooltip: { show: false },
        },
        ...categorySeries,
        {
          name: "TotalLabel",
          type: "bar",
          stack: "bridge",
          barWidth: 40,
          itemStyle: {
            color: "transparent",
            borderColor: "transparent",
          },
          emphasis: {
            itemStyle: {
              color: "transparent",
              borderColor: "transparent",
            },
          },
          label: {
            show: true,
            position: "top",
            color: "#000000",
            fontSize: 11.2,
            fontWeight: 500,
            fontFamily: "Work Sans",
            formatter: (params: any) => {
              const idx = params.dataIndex;
              if (isTotal[idx]) {
                return String(totals[idx]);
              }
              return "+" + totals[idx];
            },
          },
          data: [0, 0, 0, 0, 0],
          tooltip: { show: false },
        },
      ],
      graphic: [],
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        textStyle: {
          fontSize: 10,
        },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const total = totals[idx];
          let tooltip = `<b>${categories[idx]}</b>`;
          categoryConfig.forEach((cat) => {
            tooltip += `<br/><b>${cat.name}:</b> ${cat.values[idx]}`;
          });
          tooltip += `<br/><b>Total:</b> ${total}`;
          return tooltip;
        },
      },
    };

    chart.setOption(option);

    setTimeout(() => {
      if (chart) this.updatePhiBridgeGraphics(chart, phiBridgeData);
    }, 100);

    window.addEventListener("resize", () => {
      chart?.resize();
      setTimeout(() => {
        if (chart) this.updatePhiBridgeGraphics(chart, phiBridgeData);
      }, 100);
    });

    this.registerChart(chart);
    return chart;
  }

  updatePhiBridgeGraphics(chart: echarts.ECharts, phiBridgeData: any): void {
    const { percentChange, totals } = phiBridgeData;

    const bar1Pixel = chart.convertToPixel({ seriesIndex: 1 }, [
      1,
      totals[1],
    ]) as number[];
    const bar4Pixel = chart.convertToPixel({ seriesIndex: 1 }, [
      4,
      totals[4],
    ]) as number[];
    const topOfBar1 = chart.convertToPixel({ seriesIndex: 1 }, [
      1,
      totals[0],
    ]) as number[];

    if (!bar1Pixel || !bar4Pixel || !topOfBar1) return;

    const x1 = bar1Pixel[0];
    const x2 = bar4Pixel[0];
    const midX = (x1 + x2) / 2;

    const badgeWidth = 52;
    const badgeHeight = 22;

    const topCandidates = [bar1Pixel[1], bar4Pixel[1], topOfBar1[1]].filter(
      (v) => typeof v === "number",
    ) as number[];
    const minTop = topCandidates.length
      ? Math.min(...topCandidates)
      : topOfBar1[1];
    const extraGap = 18;
    let arrowY = minTop - (badgeHeight / 2 + extraGap);
    const minAllowedY = 8;
    if (arrowY < minAllowedY) arrowY = minAllowedY;

    chart.setOption({
      graphic: [
        {
          type: "line",
          shape: {
            x1: x1,
            y1: bar1Pixel[1],
            x2: x1,
            y2: arrowY,
          },
          style: {
            stroke: "#666666",
            lineWidth: 1,
            lineDash: [4, 3],
          },
        } as any,
        {
          type: "line",
          shape: {
            x1: x2,
            y1: bar4Pixel[1],
            x2: x2,
            y2: arrowY,
          },
          style: {
            stroke: "#666666",
            lineWidth: 1,
            lineDash: [4, 3],
          },
        } as any,
        {
          type: "line",
          shape: {
            x1: x1,
            y1: arrowY,
            x2: midX - badgeWidth / 2 - 4,
            y2: arrowY,
          },
          style: {
            stroke: "#666666",
            lineWidth: 1,
            lineDash: [4, 3],
          },
        } as any,
        {
          type: "line",
          shape: {
            x1: midX + badgeWidth / 2 + 4,
            y1: arrowY,
            x2: x2,
            y2: arrowY,
          },
          style: {
            stroke: "#666666",
            lineWidth: 1,
            lineDash: [4, 3],
          },
        } as any,
        {
          type: "polygon",
          shape: {
            points: [
              [x1, arrowY],
              [x1 + 6, arrowY - 4],
              [x1 + 6, arrowY + 4],
            ],
          },
          style: {
            fill: "#666666",
          },
        } as any,
        {
          type: "polygon",
          shape: {
            points: [
              [x2, arrowY],
              [x2 - 6, arrowY - 4],
              [x2 - 6, arrowY + 4],
            ],
          },
          style: {
            fill: "#666666",
          },
        } as any,
        {
          type: "rect",
          shape: {
            x: midX - badgeWidth / 2,
            y: arrowY - badgeHeight / 2,
            width: badgeWidth,
            height: badgeHeight,
            r: 4,
          },
          style: {
            fill: percentChange >= 0 ? "#E6F9E6" : "#FFE6E6",
          },
        } as any,
        {
          type: "text",
          style: {
            text: percentChange + "%",
            x: midX,
            y: arrowY,
            textAlign: "center",
            textVerticalAlign: "middle",
            fontSize: 9.6,
            fontWeight: 600,
            fontFamily: "Barlow",
            fill: percentChange >= 0 ? "#49764B" : "#C22423",
          },
        } as any,
      ],
    });
  }

  renderPhiBarChart(containerId: string, chartData: any) {
    if (!containerId || !chartData) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    const attribute = Object.keys(chartData)[0];
    const map = chartData[attribute] || {};

    const categories = Object.keys(map);
    const values = categories.map((k) => map[k]);

    container.innerHTML = "";
    container.style.minWidth = "";

    try {
      const parent = container.parentElement as HTMLElement | null;
      if (parent) parent.style.overflowX = "auto";
    } catch (e) {}

    const BAR_PX = 57.6;
    const desiredWidth = Math.max(
      container.clientWidth || 0,
      categories.length * BAR_PX,
    );
    container.style.minWidth = desiredWidth + "px";

    // @ts-ignore
    Highcharts.chart(containerId, {
      chart: {
        type: "column",
        backgroundColor: "transparent",
        width: desiredWidth,
      },
      title: { text: "" },
      xAxis: {
        categories: categories,
        lineColor: "#E2E7EA",
        lineWidth: 1,
        tickColor: "#E2E7EA",
        labels: {
          rotation: 0,
          style: {
            color: "#666666",
            fontSize: "11.2px",
            fontWeight: "400",
            fontFamily: "Work Sans",
          },
        },
        tickLength: 0,
      },
      yAxis: {
        title: {
          text: "PHI Score",
          style: {
            fontFamily: "Barlow",
            fontSize: "12.8px",
            fontWeight: "400",
            color: "#010907",
          },
        },
        min: 0,
        max: 100,
        gridLineColor: "#E2E7EA",
        lineWidth: 1,
        lineColor: "#E2E7EA",
        tickWidth: 1,
        tickColor: "#E2E7EA",
        labels: {
          style: {
            color: "#666666",
            fontSize: "11.2px",
            fontWeight: "400",
            fontFamily: "Work Sans",
          },
        },
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          try {
            return (
              '<div style="font-family: Work Sans; color: #666666">' +
              '<div style="font-size:11.2px; font-weight:600;">' +
              this.x +
              "</div>" +
              '<div style="font-size:10.4px;">PHI Score: <b>' +
              this.y +
              "</b></div>" +
              "</div>"
            );
          } catch (e) {
            return "PHI Score: <b>" + this.y + "</b>";
          }
        },
      },
      plotOptions: {
        column: {
          color: "#EDDAB1",
          borderColor: "#E5B629",
          borderWidth: 0,
          pointPadding: 0.15,
          groupPadding: 0.08,
          dataLabels: {
            enabled: true,
            format: "{point.y}",
            style: {
              color: "#000000",
              fontSize: "11.2px",
              fontWeight: "500",
              fontFamily: "Work Sans",
            },
          },
          point: {
            events: {
              mouseOver: function () {
                try {
                  this.update({ color: "#D1A33C" }, false);
                  this.series.chart && this.series.chart.redraw();
                } catch (e) {}
              },
              mouseOut: function () {
                try {
                  this.update({ color: "#EDDAB1" }, false);
                  this.series.chart && this.series.chart.redraw();
                } catch (e) {}
              },
            },
          },
        },
      },
      series: [
        {
          name: attribute,
          data: values,
          showInLegend: false,
        },
      ],
      credits: { enabled: false },
    });
  }

  createPhiChangeSankeyChart(
    containerId: string,
    phiChangeSankeyChartData: any,
  ) {
    if (!containerId || !phiChangeSankeyChartData) return null;
    const sankeyChartEl = document.getElementById(containerId);
    if (!sankeyChartEl) return null;

    const { quarters, categories, values, flows } = phiChangeSankeyChartData;

    const categoryColors: { [key: string]: string } = {
      Keep: "#BBDECB",
      Accelerate: "#E7DDBD",
      Optimize: "#DADADA",
      Delist: "#EAB8B8",
    };

    const sankeyLinks: any[] = [];
    flows.forEach((f: any) => {
      const [fromQ, fromCat, toQ, toCat, weight] = f;
      if (weight > 0) {
        sankeyLinks.push({
          from: `${fromQ} ${fromCat}`,
          to: `${toQ} ${toCat}`,
          weight,
          color: categoryColors[fromCat],
        });
      }
    });

    // If no flows, create minimal self-referencing links to display nodes
    const hasRealFlows = sankeyLinks.length > 0;
    if (!hasRealFlows && quarters && quarters.length > 0) {
      const firstQuarter = quarters[0];
      (categories || []).forEach((category: string) => {
        const val =
          (values && values[firstQuarter] && values[firstQuarter][category]) ||
          0;
        if (val > 0) {
          sankeyLinks.push({
            from: `${firstQuarter} ${category}`,
            to: `${firstQuarter} ${category}`,
            weight: val, // Use actual value to determine node size
          });
        }
      });
    }

    const quarterTotals: { [key: string]: number } = {};
    (quarters || []).forEach((q: string) => {
      quarterTotals[q] = (categories || []).reduce(
        (sum: number, cat: string) =>
          sum + ((values && values[q] && values[q][cat]) || 0),
        0,
      );
    });

    const nodes: any[] = [];
    (quarters || []).forEach((quarter: string, colIndex: number) => {
      (categories || []).forEach((category: string) => {
        const val =
          (values && values[quarter] && values[quarter][category]) || 0;
        nodes.push({
          id: `${quarter} ${category}`,
          color: categoryColors[category],
          column: colIndex,
          name: `${category}`,
          value: val, // Set node size based on value
          dataLabels: {
            enabled: val > 0,
            format: `${val}`,
            inside: true,
            style: {
              fontSize: "10.4px",
              fontWeight: "600",
              color:
                category === "Delist"
                  ? "#c0392b"
                  : category === "Keep"
                    ? "#27ae60"
                    : category === "Accelerate"
                      ? "#AE8F24"
                      : "#818181",
              textOutline: "none",
            },
          },
        });
      });
    });

    // Calculate margins to center single column
    const isSingleColumn = (quarters || []).length === 1;
    // For single column, center it by setting equal margins
    // Default chart width minus node width (40px) divided by 2
    const chartWidth = sankeyChartEl.offsetWidth || 800;
    const nodeWidth = 40;
    const centerMargin = isSingleColumn
      ? Math.max((chartWidth - nodeWidth - 100) / 2, 50)
      : 50;
    const chartMarginLeft = isSingleColumn ? centerMargin : 50;
    const chartMarginRight = isSingleColumn ? centerMargin : 50;

    // Render chart
    // @ts-ignore
    const chart = Highcharts.chart(sankeyChartEl, {
      chart: {
        height: 400,
        backgroundColor: "#ffffff",
        marginTop: 60,
        marginBottom: 50,
        marginLeft: chartMarginLeft,
        marginRight: chartMarginRight,
        events: {
          render: function () {
            const chart: any = this;
            if (chart.customLabels) {
              chart.customLabels.forEach((lbl: any) => lbl.destroy());
            }
            chart.customLabels = [];

            const colPositions: { [col: number]: number[] } = {};
            if (chart.series[0] && chart.series[0].nodes) {
              chart.series[0].nodes.forEach((node: any) => {
                const col = node.column;
                if (col !== undefined && node.graphic) {
                  if (!colPositions[col]) colPositions[col] = [];
                  colPositions[col].push(
                    node.graphic.getBBox().x + chart.plotLeft,
                  );
                }
              });
            }

            (quarters || []).forEach((q: string, i: number) => {
              let xPos: number;
              if (colPositions[i] && colPositions[i].length > 0) {
                const avg =
                  colPositions[i].reduce((a: number, b: number) => a + b, 0) /
                  colPositions[i].length;
                xPos = avg + 20;
              } else {
                // Handle single quarter case (when quarters.length === 1)
                if ((quarters || []).length === 1) {
                  xPos = chart.plotLeft + chart.plotWidth / 2;
                } else {
                  xPos =
                    chart.plotLeft +
                    (i / ((quarters || []).length - 1)) * chart.plotWidth;
                }
              }

              chart.customLabels.push(
                chart.renderer
                  .text(q, xPos, chart.plotTop + chart.plotHeight + 35)
                  .attr({ align: "center", zIndex: 5 })
                  .css({
                    fontSize: "11.2px",
                    fontWeight: "400",
                    color: "#666666",
                    fontFamily: "Work Sans",
                  })
                  .add(),
              );

              chart.customLabels.push(
                chart.renderer
                  .text(`<b>${quarterTotals[q]}</b>`, xPos, chart.plotTop - 10)
                  .attr({ align: "center", zIndex: 5 })
                  .css({
                    fontSize: "11.2px",
                    fontWeight: "400",
                    color: "#666666",
                    fontFamily: "Work Sans",
                  })
                  .add(),
              );
            });
          },
        },
      },
      title: { text: "" },
      xAxis: { visible: false },
      yAxis: { visible: false },
      legend: {
        enabled: true,
        align: "right",
        verticalAlign: "top",
        layout: "horizontal",
        floating: true,
        x: -10,
        y: -10,
        symbolHeight: 9.6,
        symbolWidth: 9.6,
        symbolRadius: 2,
        itemStyle: {
          fontSize: "11.2px",
          fontWeight: "400",
          color: "#666666",
          fontFamily: "Work Sans",
        },
        itemDistance: 24,
      },
      accessibility: {
        point: {
          valueDescriptionFormat:
            "{index}. {point.from} to {point.to}, {point.weight}.",
        },
      },
      tooltip: {
        headerFormat: "",
        formatter: function (this: any) {
          // Hide tooltip for self-referencing dummy flows
          if (this.point.from === this.point.to) {
            return false;
          }
          return `<b>${this.point.from}</b> → <b>${this.point.to}</b><br/>SKUs: <b>${this.point.weight}</b>`;
        },
        backgroundColor: "#000",
        style: { color: "#fff" },
        borderWidth: 0,
        shadow: false,
        useHTML: true,
      },
      series: [
        {
          // @ts-ignore
          type: "sankey",
          name: "PHI Score",
          data: sankeyLinks,
          nodes: nodes,
          // @ts-ignore
          nodeWidth: 40,
          nodePadding: 0, // Remove spacing to make it look like a stacked bar
          minLinkWidth: 2,
          dataLabels: { enabled: false },
          // @ts-ignore
          linkOpacity: hasRealFlows ? 0.25 : 0, // Hide links when showing only nodes
          curveFactor: 0.33,
          showInLegend: false,
          states: { hover: { linkOpacity: hasRealFlows ? 0.5 : 0 } },
        },
        {
          type: "scatter",
          name: "Keep",
          color: categoryColors["Keep"],
          marker: { symbol: "square", radius: 5.6 },
          showInLegend: true,
          enableMouseTracking: false,
          data: [],
        },
        {
          type: "scatter",
          name: "Accelerate",
          color: categoryColors["Accelerate"],
          marker: { symbol: "square", radius: 5.6 },
          showInLegend: true,
          enableMouseTracking: false,
          data: [],
        },
        {
          type: "scatter",
          name: "Optimize",
          color: categoryColors["Optimize"],
          marker: { symbol: "square", radius: 5.6 },
          showInLegend: true,
          enableMouseTracking: false,
          data: [],
        },
        {
          type: "scatter",
          name: "Delist",
          color: categoryColors["Delist"],
          marker: { symbol: "square", radius: 5.6 },
          showInLegend: true,
          enableMouseTracking: false,
          data: [],
        },
      ],
      credits: { enabled: false },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: [
              "downloadPNG",
              "downloadJPEG",
              "downloadPDF",
              "downloadSVG",
            ],
          },
        },
      },
    });

    return chart;
  }

  initPhiBreakdownChart(
    chartDomId: string,
    phiBreakdownData: any,
  ): echarts.ECharts | null {
    const chartDom = document.getElementById(chartDomId);
    if (!chartDom) return null;

    let chart = echarts.getInstanceByDom(chartDom);
    if (!chart) {
      chart = echarts.init(chartDom);
    } else {
      chart.clear();
    }

    const { categories, phiValues, skuCounts, isTotal } = phiBreakdownData;
    const len = categories.length;

    // Map phiValues (only for total bars) to per-bar index
    const phiByBarIndex: (number | null)[] = [];
    for (let i = 0; i < len; i++) {
      phiByBarIndex.push(isTotal[i] ? phiValues[i] : null);
    }

    // Build waterfall placeholder (invisible base) for incremental bars
    const placeholderData: number[] = [];
    let runningTotal = skuCounts[0];
    for (let i = 0; i < len; i++) {
      if (isTotal[i]) {
        placeholderData.push(0);
      } else {
        // For negative bars, start placeholder at the lower point so bar goes downward
        placeholderData.push(
          skuCounts[i] < 0 ? runningTotal + skuCounts[i] : runningTotal,
        );
        runningTotal += skuCounts[i];
      }
    }

    const barWidth = 38.4;
    const lightGold = "#EDDAB1";
    const darkGold = "#D1A33C";

    // Per-bar colors: last total bar (Target) is darker
    const barColors = categories.map((_: string, i: number) => {
      if (i === len - 1 && isTotal[i]) return darkGold;
      return lightGold;
    });

    // Dotted connector lines: only between consecutive waterfall (non-total) bars,
    // or from the first total bar into the first waterfall bar.
    // Connectors are drawn at the transition (exit) level of bar i:
    //   positive bar → top edge; negative bar → bottom edge (the level bar i+1 starts from).
    const markLineData: any[] = [];
    for (let i = 0; i < len - 1; i++) {
      // Skip whenever the NEXT bar is a total bar (e.g. Q1, Target)
      if (isTotal[i + 1]) continue;
      const topOfBar = isTotal[i]
        ? skuCounts[i]
        : placeholderData[i] + Math.max(0, skuCounts[i]);
      markLineData.push([
        { xAxis: categories[i], yAxis: topOfBar },
        { xAxis: categories[i + 1], yAxis: topOfBar },
      ]);
    }

    // Max y = highest point reached by any bar (total bars are absolute; waterfall bars stack)
    const maxY = Math.max(
      ...placeholderData.map(
        (ph: number, i: number) => ph + Math.abs(skuCounts[i]),
      ),
    );
    const yAxisMax = Math.ceil((maxY * 1.25) / 10) * 10;

    const option: echarts.EChartsOption = {
      grid: {
        left: 65,
        right: 30,
        top: 80,
        bottom: 40,
        containLabel: false,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#E2E7EA" } },
        axisLabel: {
          color: "#666666",
          fontSize: 11.2,
          fontFamily: "Work Sans",
          fontWeight: 400,
          interval: 0,
        },
      },
      yAxis: {
        type: "value",
        name: "SKUs",
        nameLocation: "middle",
        nameGap: 45,
        nameTextStyle: {
          color: "#010907",
          fontSize: 12.8,
          fontFamily: "Barlow",
          fontWeight: 400,
        },
        max: yAxisMax,
        splitLine: {
          lineStyle: { color: "#EFEFEF", type: "solid", width: 0.5 },
        },
        axisLine: { show: true, lineStyle: { color: "#E2E7EA", width: 1 } },
        axisLabel: {
          color: "#666666",
          fontSize: 11.2,
          fontFamily: "Work Sans",
          fontWeight: 400,
        },
        axisTick: { show: false },
      },
      series: [
        // Invisible placeholder for waterfall stacking
        {
          name: "Placeholder",
          type: "bar",
          stack: "breakdown",
          barWidth: barWidth,
          itemStyle: { color: "transparent", borderColor: "transparent" },
          emphasis: {
            itemStyle: { color: "transparent", borderColor: "transparent" },
          },
          data: placeholderData,
          tooltip: { show: false },
        },
        // Visible bars
        {
          name: "PHI",
          type: "bar",
          stack: "breakdown",
          barWidth: barWidth,
          itemStyle: {
            color: (params: any) => barColors[params.dataIndex],
          },
          label: {
            show: true,
            position: "inside",
            fontSize: 9.6,
            fontWeight: 400,
            fontFamily: "Work Sans",
            color: "#000000",
            formatter: (params: any) => {
              return String(skuCounts[params.dataIndex]);
            },
          },
          data: skuCounts.map((v: number) => Math.abs(v)),
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: {
              type: "dashed",
              color: "#CCCCCC",
              width: 1,
            },
            label: { show: false },
            data: markLineData,
          },
        },
        // PHI value labels above bars
        {
          name: "TopLabel",
          type: "bar",
          stack: "breakdown",
          barWidth: barWidth,
          itemStyle: { color: "transparent", borderColor: "transparent" },
          emphasis: {
            itemStyle: { color: "transparent", borderColor: "transparent" },
          },
          label: {
            show: true,
            position: "top",
            fontSize: 11.2,
            fontWeight: 500,
            fontFamily: "Work Sans",
            color: "#000000",
            formatter: (params: any) => {
              const idx = params.dataIndex;
              const phi = phiByBarIndex[idx];
              if (phi === null || phi === undefined) return "";
              return String(phi);
            },
          },
          data: new Array(len).fill(0),
          tooltip: { show: false },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          const idx = params[0]?.dataIndex;
          const phi = phiByBarIndex[idx];
          const skus = skuCounts[idx];
          const skuLabel = isTotal[idx]
            ? String(skus)
            : skus >= 0
              ? "+" + skus
              : String(skus);
          const phiLine =
            phi !== null && phi !== undefined ? `<br/>PHI: <b>${phi}</b>` : "";
          return `<b>${categories[idx]}</b><br/>SKUs: <b>${skuLabel}</b>${phiLine}`;
        },
      },
      graphic: [],
    };

    chart.setOption(option);

    setTimeout(() => {
      if (chart) this.updatePhiBreakdownGraphics(chart, phiBreakdownData);
    }, 100);

    window.addEventListener("resize", () => {
      chart?.resize();
      setTimeout(() => {
        if (chart) this.updatePhiBreakdownGraphics(chart, phiBreakdownData);
      }, 100);
    });

    this.registerChart(chart);
    return chart;
  }

  updatePhiBreakdownGraphics(
    chart: echarts.ECharts,
    phiBreakdownData: any,
  ): void {
    const { phiValues, skuCounts, isTotal, ppChange, gapToTarget } =
      phiBreakdownData;
    const len = phiValues.length;

    // Identify Baseline (first total), Q1 (second-to-last total), Target (last total)
    const baselineIdx = 0;
    let q1Idx = -1;
    let targetIdx = -1;
    for (let i = len - 1; i >= 0; i--) {
      if (isTotal[i] && targetIdx === -1) {
        targetIdx = i;
      } else if (isTotal[i] && q1Idx === -1 && targetIdx !== -1) {
        q1Idx = i;
      }
    }
    if (q1Idx === -1) q1Idx = len - 2;
    if (targetIdx === -1) targetIdx = len - 1;

    // Pixel positions: [x, y] where y = top of bar
    const baselinePx = chart.convertToPixel({ seriesIndex: 1 }, [
      baselineIdx,
      skuCounts[baselineIdx],
    ]) as number[];
    const q1Px = chart.convertToPixel({ seriesIndex: 1 }, [
      q1Idx,
      skuCounts[q1Idx],
    ]) as number[];
    const targetPx = chart.convertToPixel({ seriesIndex: 1 }, [
      targetIdx,
      skuCounts[targetIdx],
    ]) as number[];

    if (!baselinePx || !q1Px || !targetPx) return;

    const graphics: any[] = [];
    const dottedStyle = { stroke: "#999999", lineWidth: 1, lineDash: [4, 3] };
    const arrowColor = "#888888";

    // ============================================================
    // ARC from Baseline to Q1: bracket with downward arrows
    // ============================================================
    const bx = baselinePx[0]; // baseline bar center x
    const qx = q1Px[0]; // Q1 bar center x
    const midX = (bx + qx) / 2;

    // Recompute placeholderData to find the true highest stacked value
    // between baseline and Q1 (including all waterfall bars in between).
    const _placeholderData: number[] = [];
    let _running = skuCounts[baselineIdx];
    for (let i = 0; i < len; i++) {
      if (isTotal[i]) {
        _placeholderData.push(0);
      } else {
        _placeholderData.push(
          skuCounts[i] < 0 ? _running + skuCounts[i] : _running,
        );
        _running += skuCounts[i];
      }
    }
    let maxChartY = skuCounts[baselineIdx];
    for (let i = baselineIdx + 1; i <= q1Idx; i++) {
      const barTop = isTotal[i]
        ? skuCounts[i]
        : _placeholderData[i] + Math.abs(skuCounts[i]);
      if (barTop > maxChartY) maxChartY = barTop;
    }
    const maxYPxArr = chart.convertToPixel({ seriesIndex: 1 }, [
      0,
      maxChartY,
    ]) as number[];
    const highestBarTop = maxYPxArr
      ? maxYPxArr[1]
      : Math.min(baselinePx[1], q1Px[1]);

    const ppBadgeW = 51.2;
    const ppBadgeH = 17.6;
    const bracketY = highestBarTop - 50; // horizontal line Y
    const clampedBracketY = Math.max(bracketY, 8);

    // Vertical line: baseline bar top → bracket
    graphics.push({
      type: "line",
      shape: { x1: bx, y1: baselinePx[1] - 22, x2: bx, y2: clampedBracketY },
      style: dottedStyle,
    });
    // Vertical line: Q1 bar top → bracket
    graphics.push({
      type: "line",
      shape: { x1: qx, y1: q1Px[1] - 22, x2: qx, y2: clampedBracketY },
      style: dottedStyle,
    });
    // Horizontal line: left side
    graphics.push({
      type: "line",
      shape: {
        x1: bx,
        y1: clampedBracketY,
        x2: midX - ppBadgeW / 2 - 4,
        y2: clampedBracketY,
      },
      style: dottedStyle,
    });
    // Horizontal line: right side
    graphics.push({
      type: "line",
      shape: {
        x1: midX + ppBadgeW / 2 + 4,
        y1: clampedBracketY,
        x2: qx,
        y2: clampedBracketY,
      },
      style: dottedStyle,
    });
    // Downward arrow at baseline
    graphics.push({
      type: "polygon",
      shape: {
        points: [
          [bx, baselinePx[1] - 22],
          [bx - 4, baselinePx[1] - 28],
          [bx + 4, baselinePx[1] - 28],
        ],
      },
      style: { fill: arrowColor },
    });
    // Downward arrow at Q1
    graphics.push({
      type: "polygon",
      shape: {
        points: [
          [qx, q1Px[1] - 22],
          [qx - 4, q1Px[1] - 28],
          [qx + 4, q1Px[1] - 28],
        ],
      },
      style: { fill: arrowColor },
    });

    // PP change badge
    const ppPositive = ppChange >= 0;
    graphics.push({
      type: "rect",
      shape: {
        x: midX - ppBadgeW / 2,
        y: clampedBracketY - ppBadgeH / 2,
        width: ppBadgeW,
        height: ppBadgeH,
        r: 4,
      },
      style: {
        fill: ppPositive ? "#E6F9E6" : "#FFE6E6",
        stroke: ppPositive ? "#C8E6C9" : "#F5C6C6",
        lineWidth: 1,
      },
    });
    graphics.push({
      type: "text",
      style: {
        text: (ppPositive ? "+" : "") + ppChange + " pp",
        x: midX,
        y: clampedBracketY,
        textAlign: "center",
        textVerticalAlign: "middle",
        fontSize: 9.6,
        fontWeight: 600,
        fontFamily: "Barlow",
        fill: ppPositive ? "#2E7D32" : "#C62828",
      },
    });

    // ============================================================
    // GAP badge between Q1 and Target with connector lines
    // ============================================================
    const tx = targetPx[0];
    const midGapX = (qx + tx) / 2;
    const gapBadgeW = 44.8;
    const gapBadgeH = 17.6;
    // Position at the level of the higher bar top
    const gapBadgeY = Math.min(q1Px[1], targetPx[1]) - 35;
    const clampedGapY = Math.max(gapBadgeY, 8);
    const gapPositive = gapToTarget >= 0;

    // Vertical line from Q1 bar top to gap badge level
    graphics.push({
      type: "line",
      shape: { x1: qx, y1: q1Px[1] - 22, x2: qx, y2: clampedGapY },
      style: dottedStyle,
    });
    // Vertical line from Target bar top to gap badge level
    graphics.push({
      type: "line",
      shape: { x1: tx, y1: targetPx[1] - 22, x2: tx, y2: clampedGapY },
      style: dottedStyle,
    });
    // Horizontal line: Q1 to gap badge left
    graphics.push({
      type: "line",
      shape: {
        x1: qx,
        y1: clampedGapY,
        x2: midGapX - gapBadgeW / 2 - 4,
        y2: clampedGapY,
      },
      style: dottedStyle,
    });
    // Horizontal line: gap badge right to Target
    graphics.push({
      type: "line",
      shape: {
        x1: midGapX + gapBadgeW / 2 + 4,
        y1: clampedGapY,
        x2: tx,
        y2: clampedGapY,
      },
      style: dottedStyle,
    });
    // Downward arrow at Q1
    graphics.push({
      type: "polygon",
      shape: {
        points: [
          [qx, q1Px[1] - 22],
          [qx - 4, q1Px[1] - 28],
          [qx + 4, q1Px[1] - 28],
        ],
      },
      style: { fill: arrowColor },
    });
    // Downward arrow at Target
    graphics.push({
      type: "polygon",
      shape: {
        points: [
          [tx, targetPx[1] - 22],
          [tx - 4, targetPx[1] - 28],
          [tx + 4, targetPx[1] - 28],
        ],
      },
      style: { fill: arrowColor },
    });

    // Gap badge
    graphics.push({
      type: "rect",
      shape: {
        x: midGapX - gapBadgeW / 2,
        y: clampedGapY - gapBadgeH / 2,
        width: gapBadgeW,
        height: gapBadgeH,
        r: 4,
      },
      style: {
        fill: gapPositive ? "#E6F9E6" : "#FFE6E6",
        stroke: gapPositive ? "#C8E6C9" : "#F5C6C6",
        lineWidth: 1,
      },
    });
    graphics.push({
      type: "text",
      style: {
        text: (gapToTarget > 0 ? "+" : "") + gapToTarget + "%",
        x: midGapX,
        y: clampedGapY,
        textAlign: "center",
        textVerticalAlign: "middle",
        fontSize: 9.6,
        fontWeight: 600,
        fontFamily: "Barlow",
        fill: gapPositive ? "#2E7D32" : "#C62828",
      },
    });

    chart.setOption({ graphic: graphics });
  }
}
